import { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Login } from './Login';
import { SystemStatusHeader } from './SystemStatusHeader';
import { useHealth } from '../hooks/useHealth';
import { useAuth } from '../hooks/useAuth';

const GITHUB_REPO_URL = 'https://github.com/UVeye/360_lightning';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

const navItems: NavItem[] = [
  {
    to: '/overview',
    label: 'Overview',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    to: '/live',
    label: 'Live Scans',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    to: '/history',
    label: 'History',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    to: '/stats',
    label: 'Statistics',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { health, beLive, dbLive } = useHealth();
  const { authBypassActive } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-52 shrink-0 flex-col border-r border-slate-200 bg-white sticky top-0 h-screen self-start z-30">
        <div className="px-5 py-5 flex items-center gap-3">
          <Link
            to="/overview"
            className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200"
            title="360 Lightning"
          >
            <img src="/logo.png" alt="360 Lightning" className="w-6 h-6 object-contain" />
          </Link>
          <div className="leading-tight">
            <div className="text-sm font-semibold text-slate-900">360 Lightning</div>
          </div>
        </div>

        <nav className="px-3 mt-2 flex-1 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-600 font-medium'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" />
                  )}
                  <span className={isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}>
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-slate-200 text-[11px] text-slate-500 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap font-mono text-slate-400">
            {__GIT_BRANCH__ !== 'unknown' && (
              <a
                href={`${GITHUB_REPO_URL}/tree/${__GIT_BRANCH__}`}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate max-w-[8rem] hover:text-indigo-600"
                title={`Branch: ${__GIT_BRANCH__}`}
              >
                {__GIT_BRANCH__}
              </a>
            )}
            {__GIT_BRANCH__ !== 'unknown' && __GIT_COMMIT__ !== 'unknown' && (
              <span className="text-slate-300">@</span>
            )}
            {__GIT_COMMIT__ !== 'unknown' && (
              <a
                href={`${GITHUB_REPO_URL}/commit/${__GIT_COMMIT__}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-indigo-600"
                title={`Commit: ${__GIT_COMMIT__}`}
              >
                {__GIT_COMMIT__.slice(0, 7)}
              </a>
            )}
            {__GIT_BRANCH__ === 'unknown' && __GIT_COMMIT__ === 'unknown' && (
              <span className="italic text-slate-400">git info unavailable</span>
            )}
            {health?.local_build && (
              <span
                className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold tracking-wider bg-amber-50 text-amber-600"
                title="Running a local `make deploy` build, not a CI deployment"
              >
                LOCAL
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center gap-1"
              title={beLive ? 'Backend service is responding' : 'Backend service is not reachable'}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  beLive ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                }`}
              />
              <span className={beLive ? 'text-green-600' : 'text-red-600'}>
                BE {beLive ? 'online' : 'offline'}
              </span>
            </span>
            <span
              className="inline-flex items-center gap-1"
              title={dbLive ? 'Database is reachable' : 'Database is not reachable'}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  dbLive ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                }`}
              />
              <span className={dbLive ? 'text-green-600' : 'text-red-600'}>
                DB {dbLive ? 'online' : 'offline'}
              </span>
            </span>
          </div>
          {authBypassActive && (
            <div>
              <span
                className="inline-flex px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-medium"
                title="Set VITE_SKIP_AUTH=false to require Google sign-in"
              >
                Dev bypass
              </span>
            </div>
          )}
          <div className="font-mono text-slate-400">v{__APP_VERSION__}</div>
          {health?.version && (
            <div className="font-mono text-slate-400">be v{health.version}</div>
          )}
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-20">
          <div className="px-6 py-4 flex items-center gap-4 flex-wrap">
            <div className="flex-1 flex justify-start min-w-0">
              <SystemStatusHeader health={health} />
            </div>

            <div className="flex items-center gap-4 shrink-0 justify-end">
              <Link
                to="/generate"
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium text-sm shadow-lg hover:shadow-xl hover:opacity-95 transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate Scans
              </Link>

              <div className="hidden lg:block">
                <Login />
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 px-6 py-6">{children}</main>
      </div>
    </div>
  );
}
